import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Global360ViewContext } from '../../../shared/contexts/global-360-view.context';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { PinResetService } from '../../../shared/services/pin-reset.service';
import { UserDetailUiContext } from '../../shared/contexts/user-detail-ui.context';
import { CiscoHelpdeskOptions, EndUserResult, VoicemailRef } from '../../../shared/models/generated/ziroModels';
import { HelpdeskOptionsContext } from '../../shared/contexts/helpdesk-options.context';
import { CurrentClusterContext } from '../../../shared/contexts/current-cluster.context';
import { PinResetComponent, PinResetOptions } from '../pin-reset/pin-reset.component';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { HttpErrorResponse } from '@angular/common/http';
import { isEqual } from 'lodash';

interface UserPinReset {
  voicemailPinReset: string;
  extensionMobPinReset: string;
}

@Component({
  selector: 'ziro-user-pin-reset',
  templateUrl: './user-pin-reset.component.html',
  standalone: false,
})
export class UserPinResetComponent extends ZiroFormAbstractDirective<UserPinReset> implements OnInit, OnDestroy {
  private _currentClusterContext = inject(CurrentClusterContext);
  private _global360ViewContext = inject(Global360ViewContext);
  private _userDetailUiContext = inject(UserDetailUiContext);
  private _pinResetService = inject(PinResetService);
  private _helpdeskOptionsContext = inject(HelpdeskOptionsContext);
  private _toastService = inject(ToastService);
  private userDetailUiContext = inject(UserDetailUiContext);
  protected _ziroFormStateService: ZiroFormStateService;

  @ViewChild('vmPin') vmPinResetComponent: PinResetComponent;
  @ViewChild('emPin') emPinResetComponent: PinResetComponent;

  isVmPresent: boolean;
  isEmPresent: boolean;
  isLoading = true;
  hasVmError = false;
  hasEmError = false;
  voicemailMessage = '';
  extensionMobilityMessage = '';

  formConfig = {
    fields: {
      voicemailPinReset: {
        dataAutomation: 'user-voicemail-pin-reset',
        label: 'tkey;pages.pinreset.voicemail',
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.PASSWORD,
          placeholder: 'tkey;pages.reset.pin.placeholder',
        }),
        validation: [
          {
            validator: (val: string) => {
              return !val || (!!val && /(^[0-9]+$)/.test(val))
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message: 'tkey;validators.global.error.pattern',
          },
          {
            validator: () => (this.hasVmError ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID),
            message: () => this.voicemailMessage,
          },
        ],
      },
      extensionMobPinReset: {
        dataAutomation: 'user-extension-mobility-pin-reset',
        label: 'tkey;pages.pinreset.extensionmobility',
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.PASSWORD,
          placeholder: 'tkey;pages.reset.pin.placeholder',
        }),
        validation: [
          {
            validator: (val: string) =>
              !val || (!!val && /(^[0-9]+$)/.test(val))
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID,
            message: 'tkey;validators.global.error.pattern',
          },
          {
            validator: () => (this.hasEmError ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID),
            message: () => this.extensionMobilityMessage,
          },
        ],
      },
    },
  } as ZiroFormConfig;
  private _ziroIcons = ZiroIcons;
  private _cucmServerId: number;
  private _endUserResult: EndUserResult;
  private _resetPinOnNextLogin = false;
  private _voicemailRef: VoicemailRef;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    // We need UserDetailUiContext to wait until the cluster is picked from the modal.
    const subs = combineLatest([
      this._global360ViewContext.state$,
      this._currentClusterContext.state$,
      this._helpdeskOptionsContext.state$,
      this.userDetailUiContext.state$,
    ]).subscribe(([global360View, currentClusterContext, ciscoHelpdeskOptions]) => {
      this._cucmServerId = currentClusterContext.cucmServerId;
      this._endUserResult = this._global360ViewContext.getCurrentEnduser(global360View, this._cucmServerId);
      this._resetPinOnNextLogin = (ciscoHelpdeskOptions as CiscoHelpdeskOptions).resetPinOnNextLoginEnabled;
      this.isVmPresent = !!global360View.voicemails && global360View.voicemails.length > 0;
      this.isEmPresent = !!global360View.extensionMobilities && global360View.extensionMobilities.length > 0;

      if (this.isVmPresent) {
        this._voicemailRef = this._userDetailUiContext.getCurrentVoicemail();
      }
      this.isLoading = false;
    });
    this._subscriptions.add(subs);

    this.ziroFormsUpdate$.subscribe((data: ZiroFormsUpdate<UserPinReset>) => {
      if (
        data.old?.voicemailPinReset &&
        !isEqual(data.new.voicemailPinReset, data.old.voicemailPinReset) &&
        this.hasVmError
      ) {
        this.hasVmError = false;
      }
      if (
        data.old?.extensionMobPinReset &&
        !isEqual(data.new.extensionMobPinReset, data.old.extensionMobPinReset) &&
        this.hasEmError
      ) {
        this.hasEmError = false;
      }
    });
  }

  onSubmit(event: string): void {
    switch (event) {
      case PinResetOptions.END_USER: {
        this._resetEndUserPin();
        break;
      }
      case PinResetOptions.VOICEMAIL: {
        this._resetVoicemailPin();
        break;
      }
    }
  }

  submit(): Observable<null> {
    return of(null);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  private _resetVoicemailPin(): void {
    this._pinResetService
      .resetVoicemailPin(this._voicemailRef, this.entity.voicemailPinReset, this._resetPinOnNextLogin)
      .subscribe({
        next: () => {
          this._toastService.push(
            ToastTypes.SUCCESS,
            this._ziroIcons.REFRESH,
            'tkey;pages.pinreset.success.title',
            'tkey;pages.pinreset.voicemail.success.message',
          );
          this._resetVoicemailPinInput();
        },
        error: (error) => {
          if (error.status === 422) {
            this.hasVmError = true;
            this.voicemailMessage = this._pinResetService.translateVoicemailPinResetErrorResponse(error);
            this.fieldChannels['voicemailPinReset'].validateSource.next();
          } else {
            throw new HttpErrorResponse(error);
          }
          this._resetVoicemailPinInput();
        },
      });
  }

  private _resetEndUserPin(): void {
    this._pinResetService
      .resetEndUserPin(
        this._cucmServerId.toString(),
        this._endUserResult,
        this.entity.extensionMobPinReset,
        this._resetPinOnNextLogin,
      )
      .subscribe({
        next: () => {
          this._toastService.push(
            ToastTypes.SUCCESS,
            this._ziroIcons.REFRESH,
            'tkey;pages.pinreset.success.title',
            'tkey;pages.pinreset.extension_mobility.success.message',
          );
          this._resetEndUserPinInput();
        },
        error: (error) => {
          if (error.status === 422 && error.reasonCode !== 'FORBIDDEN_DN_MODIFICATION') {
            this.hasEmError = true;
            this.extensionMobilityMessage = this._pinResetService.translateEndUserPinResetErrorStatus(error);
            this.fieldChannels['extensionMobPinReset'].validateSource.next();
          } else {
            throw new HttpErrorResponse(error);
          }
          this._resetEndUserPinInput();
        },
      });
  }

  private _resetVoicemailPinInput(): void {
    this.vmPinResetComponent.isPending = false;
    this.entitySource.next({
      ...this.entity,
      voicemailPinReset: '',
    });
    this._ziroFormStateService.setIsFormDirty(false);
  }

  private _resetEndUserPinInput(): void {
    this.emPinResetComponent.isPending = false;
    this.entitySource.next({
      ...this.entity,
      extensionMobPinReset: '',
    });
    this._ziroFormStateService.setIsFormDirty(false);
  }
}
